import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Message, Progress } from 'semantic-ui-react'
import i18n from '~/components/i18n';
import MilkQuantity from './Steps/MilkQuantity';
import Services from './Steps/Services';
import GreenhouseGas from './Steps/GreenhouseGas';
import { CO2El } from '../style';
import Step from './Step';
import { FormProvider, useForm } from 'react-hook-form';
import Layout from '~/components/Layout';
import CharacteristicsOfProd from './Steps/CharacteristicsOfProd';
import { getStepPercent, handleErrors } from '~/utils/helpers';
import CO2Service, { CO2OptionsType } from '~/api/services/co2';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import VariableCosts from './Steps/VariableCosts';
import { Nullable } from '~/types';
import Results from './Steps/Results';
import FeedRations from './Steps/FeedRations';
import Cows from '@assets/images/bulls-and-cows.png'
import Calf from '@assets/images/calf.svg'
import { getNumberOfFeeds, getTransformFeedGroups, prepareDataForPayload, redistributeDays } from './utils';
import { AnsweredContainer, LogoWrapper, SubmitHeader } from './style';
import dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep'
import CalfRearing from './Steps/CalfRearing';
import EnergyModal from './Steps/EnergyModal';
import Dialog from '~/components/Dialog';
import { ThemeContext } from 'styled-components';

const FIRST_STEP_INDEX = 0
export const LAST_STEP_INDEX = 7
const FEED_GROUPS_TAB = 5
const FEED_RATIONS_TAB = 6

export const FeedPageContext = createContext(null)

//refactor
// Step content and indicator should be in the same component
// Leave only after presentation
const firstStepFields = ['totalMilkAmount', 'fat', 'protein', 'milkPrice']
const AnswerCO2Climate: React.FC = () => {
  const { t } = useTranslation()
  const stepsRef = useRef<HTMLDivElement>(null)
  const { id, farmerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { logoTitle } = useContext(ThemeContext)

  const [isBusy, setIsBusy] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [progress, setProgress] = useState(0)
  const [feedPage, setFeedPage] = useState(0)
  const [options, setOptions] = useState<Nullable<CO2OptionsType>>(null)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [energyCalcData, setEnergyCalcData] = useState(null)
  const [results, setResults] = useState(null)
  const [loadingEnergyData, setLoadingEnergyData] = useState(false)
  const [isEnergyModalOpen, setIsEnergyModalOpen] = useState(false)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)

  const methods = useForm({
    'mode': 'onSubmit'
  })

  const watchBreedField = methods.watch('breeds')
  const watchFeedGroups = methods.watch('feedGroups')
  const watchPhaseFeeding = methods.watch('phaseFeeding')

  const steps = [
    {
      title: i18n.t('Dairy data'),
      icon: '\u{1F3ED}'
    },
    {
      title: i18n.t('Operating characteristics'),
      icon: '\u{1F376}'
    },
    {
      title: i18n.t('Calf rearing'),
      image: Calf,
      style: { marginTop: 16, width: 27 }
    },
    {
      title: i18n.t('Farm fertilizer'),
      icon: '\u{1F69C}'
    },
    {
      title: i18n.t('Energy'),
      icon: '\u{26A1}'
    },
    {
      title: i18n.t('Performance groups'),
      image: Cows,
      style: { marginTop: 16 }
    },
    {
      title: i18n.t('Feed rations'),
      icon: '\u{1F33D}'
    },
    {
      title: i18n.t('GHG result'),
      icon: '\u{1F343}'
    }
  ]

  useEffect(() => {
    fetchOptions()
  }, [t, location.pathname])

  useEffect(() => {
    if (activeTab !== FEED_RATIONS_TAB && energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [activeTab, energyCalcData])

  useEffect(() => {
    if (activeTab === FEED_RATIONS_TAB) {
      setEnergyCalcData(null)
    }
  }, [watchFeedGroups])

  async function handleCo2Initial() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      if (response.data.farmer.totalMilkAmount) {
        firstStepFields.map(field => {
          methods.setValue(field, response.data.farmer[field])
        })
      }
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleDraft() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      const responseData = await CO2Service.getCO2AnswerData(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount)
      })
      if (response.data.farmer.totalMilkAmount) {
        firstStepFields.map(field => {
          methods.setValue(field, response.data.farmer[field])
        })
      }
      setActiveTab(responseData.data?.step || 0)
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleEdit() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      const responseData = await CO2Service.getCO2AnswerData(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount)
      })
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function handleExpired() {
    notify(t('Co2 climate slot expired'), 'error')
    navigate(RouteTypes.CO2_CLIMATE)
  }

  async function fetchOptions() {
    setIsBusy(true)
    try {
      /* answerStatus = 0 - initial answer (no draft, no answer yet, co2 slot active)
      answerStatus = 1 -  edit
      answerStatus = 2 - draft
      answerStatus = 3 - not available anymore (expired) */
      const responseStatus = await CO2Service.checkCo2Status(id, { farmerId })
      switch (responseStatus.data.answerStatus) {
        //initial answer (no draft, no answer yet, co2 slot active)
        case 0:
          return handleCo2Initial()
        //edit
        case 1:
          return handleEdit()
        //draft
        case 2:
          return handleDraft()
        //not available anymore (expired)
        case 3:
          return handleExpired()
      }
    } catch (error) {
      handleErrors(error)
      navigate(RouteTypes.CO2_CLIMATE)
    }
    setIsBusy(false)
  }

  useEffect(() => {
    if (stepsRef.current && activeTab <= LAST_STEP_INDEX) {
      const width = stepsRef.current.offsetWidth
      const children = Array.from(stepsRef.current.children)
      const distanceStep = children[activeTab]?.getBoundingClientRect().x - 280
      const percent = Math.floor(distanceStep / width * 100)
      setProgress(percent)
    }
  }, [activeTab, stepsRef])

  function getMessage() {
    switch (activeTab) {
      case 0:
        return t('On this page you can see your milk producer master and milk billing data for the observation period from {{periodFrom}} to {{periodTo}}, \n\
        which is stored at {{companyName}}.  The data from the milk bills (such as protein content, fat content, etc.) \n\
        are cumulative and volume-weighted data over the observation period',
        {
          periodFrom: dayjs(options?.co2Footprint?.periodFrom).format('DD.MM.YYYY'),
          periodTo: dayjs(options?.co2Footprint?.periodTo).format('DD.MM.YYYY'),
          companyName: options?.farmer?.companyName
        })
      case 1:
        return t('When the breed is selected, the calculation basis is loaded by the Bavarian State Institute for Agriculture (LfL). \n\
        Please enter your individual operating parameters. If you have several breeds on your farm, you can indicate this by clicking on another breed with the percentage share. \n\
        The key figures should still reflect the average inventory.')
      case 2:
        return t('Please enter all the necessary data for raising calves on your farm on this page.')
      case 3:
        return t('Please enter the information about storing your manure here.')
      case 4:
        return t('Caution: If you feed in all of the electricity from a PV system, for example, and then draw it back from the electricity grid for your use, \n\
        you must NOT include the share of the PV system. You can only enter electricity that you generate yourself and \n\
        that you use directly yourself. The same applies to biogas plants. Reason: Electricity fed in is credited to the energy sector and may not be mentioned again for dairy farming.')
      case 5:
        if (feedPage === 0) {
          return t('Define your performance groups in this area. The performance groups “Dry Cow” and “Lactating 1” are already defined as standard. \n\
          If you would like to define additional performance groups, please click on "+". \n\
          You will be asked to indicate the number of days that the average animal in this performance group is fed. The sum of the days of all performance groups must be equal to the calving interval.')
        } else if (feedPage === 2) {
          return t('The corresponding feedstuffs must now be assigned to the previously defined feed groups. To do this, open each individual feed group and select the corresponding feed. \n\
        If you want to change the feed groups, click on “Adjust feed groups” and make changes there.')
        }
        return ''
      case 6:
        return t('The table shows the performance groups and assigned feed you defined in the previous step. Enter the corresponding value in “kg FM/animal per day” for each field. \n\
        Alternatively, it is possible to specify the values ​​for coarse feed in “kg DM/animal and day”. Optionally, you can also specify your own energy content in “MJ/NEL”.')
      case 7:
        return `Für eine Treibhausgasbilanzierung werden alle bei der Herstellung dieses Produktes (Milch)
        entstehenden Treibhausgase (THG) erfasst. Dabei werden sowohl die im landwirtschaftlichen
        Betrieb entstehenden direkten und indirekten Emissionen als auch die über den Zukauf von
        Betriebsmitteln importierten THG-Emissionen berücksichtigt. Aus der Summe aller emittierten
        Treibhausgase wird zum Schluss der CO2-Fußabdruck berechnet.
        Für die Landwirtschaft sind die Treibhausgase Methan (CH4), Lachgas (N2O) und
        Kohlenstoffdioxid (CO2) relevant. Treibhausgase unterscheiden sich hinsichtlich ihrer
        Verweildauer in der Atmosphäre und ihrer Klimaschädlichkeit. Aus diesem Grund werden sie in
        CO2-Äquivalente umgerechnet und angegeben.
        Die Systemgrenzen der THG-Bewertung Milchkuhhaltung sind wie folgt gesetzt: Die
        Bewertung erfolgt für die Milchproduktion bis zur Milch im Tank, dem Tierverkauf ab Stalltür und
        dem Wirtschaftsdünger im Lager. Es werden die THG-Emissionen der Milcherzeugung sowie die
        vorgelagerten THG-Emissionen aus der Herstellung der eingesetzten Betriebsmittel
        berücksichtigt (LfL 2024).`
      default:
        return ''
    }
  }

  function renderSteps() {
    return <CO2El.Steps ref={stepsRef}>
      {steps.map((step, index) => {
        return <Step
          step={step}
          index={index}
          key={step.title}
          handleStepClick={() => setActiveTab(index)}
          active={activeTab === index}
          isLoading={activeTab === index && isLoadingResults}
          isLoadingResults={isLoadingResults}
          breed={watchBreedField?.length > 0}
        />
      })}
    </CO2El.Steps>
  }

  function renderActiveComponentStep() {
    switch (activeTab) {
      case 0:
        return <CharacteristicsOfProd
          options={options}
          setOptions={setOptions}
        />
      case 1:
        return <MilkQuantity
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
          breeds={options?.breeds || []}
          housingTypes={options?.housingTypes || []}
        />
      case 2:
        return <CalfRearing
          calfRearingTypes={options?.calfRearingTypes || []}
        />
      case 3:
        return <Services
          manureTypes={options?.manureTypes || []}
          manureStorageTypes={options?.manureStorageTypes || []}
        />
      case 4:
        return <GreenhouseGas
          energyConsumptions={options?.energyConsumptions || []}
        />
      case 5:
        return <VariableCosts milkOptions={options} />
      case 6:
        return <FeedRations
          milkOptions={options}
        />
      case 7:
        return <Results
          results={results}
          removeMargin
        />
    }
  }

  async function handleSubmitCo2Answers(data) {
    const calvingPeriodDays = methods.getValues('calvingPeriod')
    data.feedGroups = data.feedGroups?.length > 1 ? redistributeDays(data.feedGroups, calvingPeriodDays) : data.feedGroups
    const payload = prepareDataForPayload(data)
    setIsBusy(true)
    try {
      await CO2Service.storeCO2Answers(id, { ...payload, farmerId })
      setIsSubmitModalOpen(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleSubmitNextStep() {
    if (activeTab === FEED_GROUPS_TAB) {
      const feedsGroup = methods.getValues('feedGroups')
      const phaseEnabled = methods.getValues('phaseFeeding')
      const feedGroups = phaseEnabled ? getTransformFeedGroups(feedsGroup) : feedsGroup
      let hasErrors = false
      let countCoarseFeeds = 0
      feedGroups.map(feedGroup => {
        const numberOfFeedsAssign = getNumberOfFeeds(feedGroup);
        countCoarseFeeds += feedGroup?.coarseFeeds?.length
        if (numberOfFeedsAssign === 0) {
          const feedElement = document.getElementById(feedGroup?.name);
          if (feedElement) {
            feedElement.classList.add('error-feed-group');
            hasErrors = true
          }
        }
      });
      if (!hasErrors && countCoarseFeeds > 0) {
        setActiveTab(activeTab + 1);
        handleSaveDraft()
      }
      if (countCoarseFeeds === 0) {
        notify(t('Please select at least one coarse feed'), 'error')
      }
      return
    }
    setActiveTab(activeTab + 1)
    handleSaveDraft()
  }

  async function onSubmit(data) {
    if (activeTab === LAST_STEP_INDEX) {
      handleSubmitCo2Answers(cloneDeep(data))
    } else if (activeTab + 1 === LAST_STEP_INDEX) {
      setActiveTab(activeTab + 1)
      await handleLoadResults(cloneDeep(data))
    } else {
      handleSubmitNextStep()
    }
  }

  async function handleLoadResults(data, disableSave = false) {
    const calvingPeriodDays = methods.getValues('calvingPeriod')
    data.feedGroups = data.feedGroups?.length > 1 ? redistributeDays(data.feedGroups, calvingPeriodDays) : data.feedGroups
    setIsLoadingResults(true)
    try {
      const payload = prepareDataForPayload(data)
      const response = await CO2Service.getCO2Results(id, { ...payload, farmerId })
      if (!disableSave) {
        await CO2Service.saveCo2Draft(id, { ...payload, step: activeTab, farmerId })
      }
      setResults(response.data)
    } catch (error) {
      setActiveTab(activeTab - 1)
      handleErrors(error)
    }
    setIsLoadingResults(false)
  }

  function renderPreviousButton() {
    return <Button
      type='button'
      disabled={isLoadingResults}
      onClick={() => setActiveTab(activeTab - 1)}
      content={t('Previous')}
    />
  }

  function renderFeedRationsButton() {
    if (energyCalcData.energyAddition < 0 || energyCalcData.energyAddition >= 100) {
      return renderCalcEnergyButton()
    }
    return <Button
      type={'submit'}
      content={t('Calculate GHG emissions')}
      style={{ minWidth: 250 }}
    />
  }

  function renderNextButton() {
    return activeTab === FEED_RATIONS_TAB ? renderFeedRationsButton() : <Button
      type={'submit'}
      loading={isLoadingResults}
      disabled={!enableNextButton}
      content={t('Next')}
    />
  }

  function renderSubmitButton() {
    return <Button
      style={{ minWidth: 225 }}
      disabled={Object.keys(methods.formState.errors).length > 0 || isLoadingResults}
      content={t('Complete data collection')}
    />
  }

  function renderAssignFeedButton() {
    return <Button
      type={'button'}
      style={{ minWidth: 200 }}
      content={t('Assign feeds')}
      onClick={() => setFeedPage(watchPhaseFeeding === 1 ? 1 : 2)}
    />
  }

  function renderBackToFeedGroups() {
    return <Button
      type={'button'}
      style={{ minWidth: 210 }}
      content={t('Back to performance groups')}
      onClick={() => setFeedPage(0)}
    />
  }

  async function getEnergyData() {
    const data = methods.getValues()
    const calvingPeriodDays = methods.getValues('calvingPeriod')
    data.feedGroups = data.feedGroups?.length > 1 ? redistributeDays(data.feedGroups, calvingPeriodDays) : data.feedGroups
    const payload = prepareDataForPayload(data)
    setLoadingEnergyData(true)
    try {
      const response = await CO2Service.calculateEnergyData(id, { ...payload, farmerId })
      setIsEnergyModalOpen(true)
      setEnergyCalcData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingEnergyData(false)
  }

  function renderCalcEnergyButton() {
    return <Button
      type='button'
      content={t('Calculate energy requirements')}
      onClick={() => getEnergyData()}
      loading={loadingEnergyData}
      disabled={loadingEnergyData}
      style={{ minWidth: 250 }}
    />
  }

  function renderAnwseredPercent() {
    const percent = getStepPercent(activeTab)
    return <AnsweredContainer>
      <Progress precision={2} percent={percent} progress />
    </AnsweredContainer>
  }

  async function handleSaveDraft() {
    const data = methods.getValues()
    const calvingPeriodDays = methods.getValues('calvingPeriod')
    if (data.feedGroups && data.feedGroups?.length > 1) {
      data.feedGroups = redistributeDays(data.feedGroups, calvingPeriodDays)
    }
    const payload = prepareDataForPayload(data)
    try {
      await CO2Service.saveCo2Draft(id, { ...payload, step: activeTab + 1, farmerId })
    } catch (error) {
      // do not display errors to the user cause it will confuse him
      // handleErrors(error)
    }
  }

  function renderEnergyModal() {
    return <EnergyModal
      energyCalcData={energyCalcData}
      isEnergyModalOpen={isEnergyModalOpen}
      handleClose={() => setIsEnergyModalOpen(false)}
    />
  }

  function renderSubmitModal() {
    const slotEnd = dayjs(options?.co2Footprint?.end).format('dddd, D MMMM YYYY HH:mm')
    return <Dialog
      textHeader={''}
      isDialogOpen={isSubmitModalOpen}
      textSubmit={t('Back to home page')}
      hideCloseIcon
      handleSubmit={() => {
        setIsSubmitModalOpen(false)
        navigate(RouteTypes.CO2_CLIMATE)
      }
      }
      textContent={<div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <SubmitHeader>{'Vielen Dank!'}</SubmitHeader>
          <div>{'Ihre Eingabe wurde erfolgreich übermittelt.'}</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>{`Sie bekommen nach dem Ende des Erhebungszeitraumes ${slotEnd} Uhr eine E-Mail mit Ihren Ergebnissen zugesendet.`}</div>
          <div>{'In den nächsten Wochen erhalten Sie zusätzlich eine ausführliche Auswertung,'}</div>
          <div>{'in der u.a. verschiedene Vergleichsmöglichkeiten bereitgestellt werden.'}</div>
          <div>{'Sobald die Auswertung verfügbar ist, erhalten Sie von uns nochmal eine Information per Mail.'}</div>
        </div>
        <div style={{ marginBottom: 20 }}>{'Sie können sich nun abmelden.'}</div>
        <LogoWrapper style={{ backgroundImage: `url(${logoTitle})` }} />
      </div>}
    />
  }

  // we cannon procced if we have not selected breed unless we are on first step and on the last step we have to submit
  const enableNextButton = activeTab === 0 || (activeTab < LAST_STEP_INDEX && Boolean(watchBreedField) && watchBreedField?.length > 0)
  // we are on 4th step and we are on first feeding page
  const assignFeedButton = (activeTab === FEED_GROUPS_TAB) && (feedPage === 0)

  const backToFeedGroups = (activeTab === FEED_GROUPS_TAB) && (feedPage === 2)

  const displayEnergyCalcData = activeTab === FEED_RATIONS_TAB && (!energyCalcData || (energyCalcData?.energyAddition < 0 || energyCalcData?.energyAddition >= 100))

  return (
    <Layout
      title={<span dangerouslySetInnerHTML={{ __html: t('CO2 Klima Check') }} />}
      segmented
      close={RouteTypes.CO2_CLIMATE}
      isBusy={isBusy}
      isCo2Loader={true}
      actions={renderAnwseredPercent()}
    >
      {renderEnergyModal()}
      {renderSubmitModal()}
      <CO2El.StepsContainer>
        <Progress
          percent={progress}
          size='tiny'
          active
          style={{
            position: 'absolute',
            width: '100%',
            top: 23
          }}
        />
        {renderSteps()}
        <Message info style={{
          borderRadius: '10px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: 'fit-content',
          textAlign: 'center',
          marginTop: 42
        }}>
          <Message.Content>{getMessage()}</Message.Content>
        </Message>
      </CO2El.StepsContainer>
      <CO2El.WizzardContentWrapper onSubmit={methods.handleSubmit(onSubmit)}>
        <CO2El.StyledContent $isFirstStep={activeTab === 0}>
          <FeedPageContext.Provider value={{ feedPage, setFeedPage, energyCalcData, setEnergyCalcData, loadingEnergyData }}>
            <FormProvider {...methods}>
              {!isBusy && options && renderActiveComponentStep()}
            </FormProvider>
          </FeedPageContext.Provider>
        </CO2El.StyledContent>
        <CO2El.ActionsContainer $activeTab={activeTab}>
          {activeTab > FIRST_STEP_INDEX && !backToFeedGroups && renderPreviousButton()}
          {backToFeedGroups && renderBackToFeedGroups()}
          {enableNextButton && !assignFeedButton && !displayEnergyCalcData && renderNextButton()}
          {assignFeedButton && renderAssignFeedButton()}
          {activeTab === LAST_STEP_INDEX && renderSubmitButton()}
          {displayEnergyCalcData && renderCalcEnergyButton()}
        </CO2El.ActionsContainer>
      </CO2El.WizzardContentWrapper>
    </Layout >
  );
};

export default AnswerCO2Climate;